import React from "react";

const TYPE_META = {
  attaque: { icon: "⚔️", label: "Attaque", color: "bg-rose-600" },
  debuff: { icon: "🌀", label: "Sort", color: "bg-fuchsia-600" },
  soin: { icon: "💚", label: "Soin", color: "bg-emerald-600" },
};

const STAT_LABELS = {
  atk: "ATK", def: "DEF", vit: "VIT", precision: "Précision",
  crit: "CRIT", degatsElem: "Dég. Élém.", res: "RÉS",
};

// Construit une description lisible de l'effet d'une compétence ennemie
const describeAbility = (a) => {
  const parts = [];
  if (a.type === "attaque") {
    parts.push(`Attaque ×${a.multiplier || 1} dégâts`);
  } else if (a.type === "soin") {
    parts.push(`Se soigne de ${Math.round((a.selfHealPercent || 0.15) * 100)}% PV`);
  } else if (a.type === "debuff") {
    parts.push("Sort néfaste");
  }
  const ap = a.applyOnPlayer;
  if (ap) {
    if (ap.brulure) parts.push(`inflige Brûlure (${ap.brulure.duration} t)`);
    if (ap.stun) parts.push(`Étourdit (${ap.stun.duration} t)`);
    if (ap.gel) parts.push(`Gèle (${ap.gel.duration} t)`);
    const cb = Array.isArray(ap.combatBuff) ? ap.combatBuff : ap.combatBuff ? [ap.combatBuff] : [];
    for (const b of cb) {
      parts.push(`${b.value > 0 ? "+" : ""}${b.value} ${STAT_LABELS[b.stat] || b.stat}`);
    }
  }
  return parts.join(" · ");
};

// Affiche les compétences de l'ennemi sous forme de petits carrés.
// Carré grisé + nombre de tours si en récupération (cooldown). Infobulle au survol.
export const EnemyAbilities = ({ competences = [], cooldowns = {} }) => {
  if (!competences || competences.length === 0) return null;
  return (
    <div data-testid="enemy-abilities" className="mt-2 flex items-center justify-center gap-1.5">
      {competences.map((a) => {
        const cd = cooldowns[a.id] || 0;
        const onCd = cd > 0;
        const meta = TYPE_META[a.type] || TYPE_META.attaque;
        return (
          <div key={a.id} className="relative group">
            <div
              data-testid={`enemy-ability-${a.id}`}
              className={`relative w-8 h-8 rounded-lg border-2 border-slate-950 flex items-center justify-center text-sm cartoon-shadow-static transition-all ${
                onCd ? "bg-slate-700 opacity-50 grayscale" : `${meta.color} opacity-100`
              }`}
              title={`${a.nom} — ${describeAbility(a)}${onCd ? ` (récup. ${cd})` : ""}`}
            >
              <span>{meta.icon}</span>
              {onCd && (
                <span className="absolute -bottom-1 -right-1 bg-slate-950 text-yellow-300 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center border border-yellow-500">
                  {cd}
                </span>
              )}
            </div>
            {/* Infobulle stylée au survol */}
            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-44 z-50 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-slate-950 border-2 border-slate-700 rounded-lg px-2.5 py-1.5 text-left cartoon-shadow-static">
                <p className="font-display font-bold text-xs text-slate-50 flex items-center gap-1">
                  <span>{meta.icon}</span> {a.nom}
                </p>
                <p className="text-[11px] text-slate-300 leading-snug mt-0.5">{describeAbility(a)}</p>
                {onCd ? (
                  <p className="text-[10px] text-amber-400 mt-0.5 font-bold">En récupération : {cd} tour{cd > 1 ? "s" : ""}</p>
                ) : (
                  <p className="text-[10px] text-emerald-400 mt-0.5 font-bold">Prête</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EnemyAbilities;
